import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthCard } from '../components/auth/AuthCard';
import { OAuthButtons } from '../components/auth/OAuthButtons';
import { Button } from '../components/shared/Button';
import { Eye, EyeOff, LogIn, CheckCircle, AlertCircle, X, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  email?: string;
  password?: string;
}

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login: apiLogin } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('error') === 'google_failed') {
      setErrorMessage(t('auth.oauth.googleFailed'));
    }
  }, [searchParams, t]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t('validation.emailRequired');
    }

    if (!formData.password) {
      newErrors.password = t('validation.password');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await apiLogin({
        email: formData.email.trim(),
        password: formData.password,
      });
      setSuccessMessage(t('auth.login.success'));
      setTimeout(() => navigate(redirectTo), 400);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t('auth.login.error');
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Clear general error message
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  const dismissError = () => {
    setErrorMessage(null);
  };

  return (
    <AuthLayout
      variant="login"
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
    >
      <AuthCard
        label={t('auth.login.cardLabel')}
        labelColor="primary"
        title={t('auth.login.cardTitle')}
        subtitle={t('auth.login.cardSubtitle')}
        footer={{
          text: t('auth.login.noAccount'),
          linkText: t('auth.login.register'),
          href: '/register',
        }}
      >
        {/* Success Message */}
        {successMessage && (
          <div 
            className="flex items-center gap-3 rounded-2xl border border-success/25 bg-success/10 p-4 text-success"
            role="alert"
            aria-live="polite"
          >
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
        )}

        {/* Error Message Banner */}
        {errorMessage && (
          <div 
            className="flex items-center gap-3 rounded-2xl border border-error/25 bg-error/10 p-4 text-error"
            role="alert"
            aria-live="assertive"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium flex-1">{errorMessage}</span>
            <button
              onClick={dismissError}
              className="p-1 hover:bg-error/10 rounded-full transition-colors"
              aria-label={t('common.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.login.email')}
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder={t('auth.login.emailPlaceholder')}
              autoComplete="email"
              className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                errors.email ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
              }`}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <p id="email-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                {t('auth.login.password')}
              </label>
              <Link 
                to="/reset-password" 
                className="text-sm text-primary hover:underline"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={t('auth.login.passwordPlaceholder')}
                autoComplete="current-password"
                className={`w-full rounded-2xl border px-4 py-3 pr-12 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  errors.password ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
                }`}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? t('common.hide') : t('common.show')}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && (
              <p id="password-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.password}
              </p>
            )}
          </div>

          {/* Remember Me Checkbox */}
          <div className="flex items-center gap-3">
            <input
              id="rememberMe"
              type="checkbox"
              checked={formData.rememberMe}
              onChange={(e) => handleChange('rememberMe', e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
            />
            <label htmlFor="rememberMe" className="cursor-pointer text-sm text-slate-700">
              {t('auth.login.rememberMe')}
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting}
            icon={<LogIn className="w-5 h-5" />}
          >
            {isSubmitting ? `${t('auth.login.cta')}...` : t('auth.login.cta')}
          </Button>

          {/* OAuth Buttons */}
          <OAuthButtons action="login" />

          {/* Back to Home Link */}
          <div className="text-center pt-2">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-primary"
            >
              <Home className="w-4 h-4" />
              {t('auth.login.backToHome')}
            </Link>
          </div>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default LoginPage;
